<template>
  <div
    v-if="options.dialog"
    class="swal2-container swal2-center swal2-backdrop-show"
    :style="options.containerStyle"
  >
    <div
      class="swal2-popup swal2-modal swal2-show d-grid"
      style="display: grid;"
      :style="options.popupStyle"
    >
      <div
        v-if="options.showSwalType"
        :class="`swal2-${options.swalType}`"
        class="swal2-icon swal2-icon-show d-flex"
      >
        <div
          v-if="options.swalType !== 'error'"
          class="swal2-icon-content"
        >
          {{ options.swalIcon }}
        </div>

        <span v-else class="swal2-x-mark">
          <span class="swal2-x-mark-line-left" />
          <span class="swal2-x-mark-line-right" />
        </span>
      </div>

      <div
        id="swal2-html-container"
        class="swal2-html-container d-block"
      >
        <div v-html="options.message" />
        <component
          :is="options.component"
          v-if="options.component"
          v-bind="options.component_data"
          @resolveData="setResolveData"
        />
      </div>

      <div
        ref="swal2Actions"
        class="swal2-actions"
        :class="{'submit-only': !options.showCancel}"
      >
        <button
          v-if="options.showSubmit"
          type="button"
          class="btn btn--tertiary"
          @click="agree(null)"
        >
          {{ options.submitText }}
        </button>

        <button
          v-for="(extraBtn, index) in options.extraButtons"
          :key="index"
          type="button"
          :class="extraBtn.classes"
          @click="agree(extraBtn.resolveData)"
        >
          {{ extraBtn.text }}
        </button>

        <button
          v-if="options.showCancel"
          ref="cancelBtn"
          type="button"
          class="btn btn--primary"
          @click="cancel"
        >
          {{ options.cancelText }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useModalStore } from '@/stores/modals'
import type { ConfirmationOptions, SwalType } from '@/misc/custom-types'

interface Props {
  name?: string,
  usePinia?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  name: 'confirm',
  usePinia: true,
})

const cancelBtn = ref()
const swal2Actions = ref()

const options = ref<ConfirmationOptions>({
  resolveData: [],
  dialog: false,
  title: null,
  swalType: 'warning',
  swalIcon: '!',
  reject: null,
  message: null,
  submitText: null,
  cancelText: null,
  component: null,
  component_data: {},
  popupStyle: {},
  containerStyle: {},
  extraButtons: [],
  showCancel: true,
  showSubmit: true,
  showSwalType: true,
  resolve: undefined
})

const beforeOpen: () => Promise<boolean> = async () => {
  return new Promise((resolve) => {
    try {
      if (props.usePinia) {
        useModalStore().create({
          name: props.name,
          closeByEscape: true
        })
      }

      resolve(true)
    } catch (e: any) {
      console.error(e)
      resolve(e)
    }
  })
}

const open = (
  {
    message = null,
    title = null,
    component = null,
    component_data = {},
    submitText = 'Verwijderen',
    cancelText = 'Sluiten',
    popupStyle = {},
    containerStyle = {},
    showCancel = true,
    showSubmit = true,
    showSwalType = true,
    swalType = 'warning' as SwalType,
    swalIcon = '!',
    extraButtons = []
  }: Partial<ConfirmationOptions>
) => {
  return new Promise((resolve, reject) => {
    beforeOpen()
      .then(() => {
        options.value.message = message
        options.value.title = title
        options.value.component = component
        options.value.component_data = component_data
        options.value.submitText = submitText
        options.value.cancelText = cancelText
        options.value.showCancel = showCancel
        options.value.showSubmit = showSubmit
        options.value.popupStyle = popupStyle
        options.value.containerStyle = containerStyle
        options.value.showSwalType = showSwalType
        options.value.swalType = swalType
        options.value.swalIcon = swalIcon
        options.value.extraButtons = extraButtons

        options.value.resolve = resolve

        // component renders when all data isset, otherwise previous data will be used
        options.value.dialog = true

        setTimeout(() => {
          if (options.value.showCancel) cancelBtn.value?.focus()

          createFocusTrap(swal2Actions.value)
        }, 150)

        setTimeout(() => document.addEventListener('keyup', onKeyUp), 250)
      })
      .catch(() => reject())
  })
}

const createFocusTrap = (el: HTMLElement | null) => {
  if (!options.value.dialog || !el) return

  el.addEventListener('keydown', focusTrapListener)
}

const removeFocusTrap = (el: HTMLElement | null) => {
  if (!options.value.dialog || !el) return

  el.removeEventListener('keydown', focusTrapListener)
}

const focusTrapListener = (e: KeyboardEvent) => {
  if (e.key !== 'Tab') return

  const focusableEls = (swal2Actions.value as Element).querySelectorAll('a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input[type="text"]:not([disabled]), input[type="radio"]:not([disabled]), input[type="checkbox"]:not([disabled]), select:not([disabled])')

  const firstFocusableEl = focusableEls[0] as HTMLElement
  const lastFocusableEl = focusableEls[focusableEls.length - 1] as HTMLElement

  if (
    (e.shiftKey && document.activeElement === firstFocusableEl) ||
    (!e.shiftKey && document.activeElement === lastFocusableEl)
  ) {
    const newFocusableEl = e.shiftKey ? lastFocusableEl : firstFocusableEl
    newFocusableEl.focus()
    e.preventDefault()
  }
}

const agree = (data = null) => {
  if (!options.value.resolve) return

  options.value.resolve({
    value: true,
    data: data ?? options.value.resolveData
  })

  closing()
}

const cancel = () => {
  if (!options.value.resolve) return

  options.value.resolve({
    value: false,
    data: undefined
  })

  closing()
}

const resetData = () => {
  options.value.message = null
  options.value.component = null
  options.value.component_data = {}
  options.value.submitText = null
  options.value.cancelText = null
  options.value.showCancel = true
  options.value.popupStyle = {}
  options.value.containerStyle = {}
  options.value.swalType = 'warning'
  options.value.swalIcon = '!'
  options.value.extraButtons = []
  options.value.title = null
  options.value.resolve = undefined
  options.value.reject = null
  options.value.resolveData = []
  options.value.dialog = false
}

const closing = () => {
  document.removeEventListener('keyup', onKeyUp)

  removeFocusTrap(swal2Actions.value)

  if (props.usePinia) {
    useModalStore().remove(props.name)
  }

  resetData()
}

const onKeyUp = (event) => {
  const lastOpen = props.usePinia ? useModalStore().latestOpenModalName : props.name

  if ((event.code === 'Enter' || event.code === 'NumpadEnter') && (props.name === lastOpen) && options.value.dialog) {
    agree()
  }

  if (event.code === 'Escape' && (props.name === lastOpen) && options.value.dialog) {
    cancel()
  }
}

const setResolveData = (data) => {
  options.value.resolveData = data
}

defineExpose({
  open,
  agree,
  cancel,
  closing,
  resetData,
  setResolveData
})
</script>

<style scoped lang="scss">
.swal2-icon {
  display: flex;
  margin-top: 2rem;
}

.swal2-html-container {
  margin-bottom: 0;
}

.swal2-actions {
  margin-top: 0;
  margin-bottom: 0.75em;
}

.submit-only {
  margin: 0.5em auto 1rem auto !important;
}

.submit-only .btn {
  margin: 0 !important;
}
</style>
